import type { ChapterDef, EndingDef } from "@plotjack/core";
import { titleArtUrl, layerSet } from "../art/assets";
import type { RunScope } from "@plotjack/core";
import { SceneStage } from "./SceneStage";

interface Props {
  quest?: string;
  premise?: string;
  endings: EndingDef[];
  chapters: ChapterDef[];
  chosenEnding: string;
  onChooseEnding: (id: string) => void;
  runScope: RunScope;
  onRunScope: (scope: RunScope) => void;
  chapterId: string;
  onChapterId: (id: string) => void;
  chaptersCompleted: string[];
  hasContinue: boolean;
  onStart: () => void;
  onContinue: () => void;
  onHowToPlay: () => void;
  memories: string[];
}

export function TitleScreen({
  quest,
  premise,
  endings,
  chapters,
  chosenEnding,
  onChooseEnding,
  runScope,
  onRunScope,
  chapterId,
  onChapterId,
  chaptersCompleted,
  hasContinue,
  onStart,
  onContinue,
  onHowToPlay,
  memories,
}: Props) {
  return (
    <section className="screen title-screen" aria-labelledby="title-heading">
      {layerSet("title_keyart", "calm") ? (
        <div className="title-art title-art--live">
          <SceneStage
            sceneId="title_keyart"
            mood="calm"
            glitch={false}
            showpiece
          />
        </div>
      ) : (
        titleArtUrl() && (
          <div className="title-art" aria-hidden="true">
            <img
              src={titleArtUrl()!}
              alt=""
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          </div>
        )
      )}
      <div className="title-glow" />
      <header className="title-header">
        <p className="eyebrow">Plotjack Solo</p>
        <h1 id="title-heading">Narrator Quits</h1>
        <p className="subtitle">
          A candlelit fantasy the narrator swears is serious. Your cards
          disagree.
        </p>
        {quest && <p className="title-quest">{quest}</p>}
        {premise && <p className="title-stakes">{premise}</p>}
      </header>
      <div className="panel setup-panel">
        <h2>Run length</h2>
        <div className="run-scope" role="radiogroup" aria-label="Run length">
          <button
            type="button"
            role="radio"
            aria-checked={runScope === "full"}
            className={`btn ${runScope === "full" ? "btn-primary" : ""}`}
            onClick={() => onRunScope("full")}
          >
            Full book (~45 min)
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={runScope === "chapter"}
            className={`btn ${runScope === "chapter" ? "btn-primary" : ""}`}
            onClick={() => onRunScope("chapter")}
          >
            Single chapter (~15 min)
          </button>
        </div>
        {runScope === "chapter" && (
          <div className="chapter-select">
            <h3>Chapter</h3>
            <div className="ending-grid">
              {chapters.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  className={`ending-card ${chapterId === ch.id ? "ending-card--active" : ""}`}
                  onClick={() => onChapterId(ch.id)}
                >
                  <span className="ending-card__title">
                    Ch. {ch.number}: {ch.title}
                    {chaptersCompleted.includes(ch.id) ? " (cleared)" : ""}
                  </span>
                  <span className="ending-card__desc">{ch.subtitle}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <h2>Chosen Ending</h2>
        <p className="panel-help">
          Pick the ending you steer toward before Dread hits 10.
        </p>
        <div
          className="ending-grid"
          role="radiogroup"
          aria-label="Chosen ending"
        >
          {endings.map((e) => (
            <button
              key={e.id}
              type="button"
              role="radio"
              aria-checked={chosenEnding === e.id}
              className={`ending-card ${chosenEnding === e.id ? "ending-card--active" : ""}`}
              onClick={() => onChooseEnding(e.id)}
            >
              <span className="ending-card__title">{e.title}</span>
              <span className="ending-card__desc">{e.description}</span>
            </button>
          ))}
        </div>
        <div className="title-actions">
          <button type="button" className="btn btn-ghost" onClick={onHowToPlay}>
            How to play
          </button>
          {hasContinue && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onContinue}
            >
              Continue saved run
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={onStart}>
            Begin Run
          </button>
        </div>
      </div>
      {memories.length > 0 && (
        <aside className="panel memory-panel" aria-label="Narrator memories">
          <h2>The Narrator Remembers</h2>
          <ul>
            {memories.slice(-3).map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </aside>
      )}
    </section>
  );
}
